import { PageController } from '@client/components/_core/layout/PageController';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { User } from '@client/components/_ui/display/User';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/compensation-payments',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Linked payments',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CompensationPayments />
        </PageController>
    );
}

type QueryParams = RouterInputs['compensationPayment']['listCompensationPayments'];

const columnHelper = createColumnHelper<
    RouterOutputs['compensationPayment']['listCompensationPayments']['rows'][number],
    QueryParams['orderBy']
>();

function CompensationPayments() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const { reserveId } = Route.useParams();
    const { formatDate, formatCurrency } = useFormatter();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({
        orderBy: { id: 'desc' },
    });

    const { data, isLoading, error } = trpc.compensationPayment.listCompensationPayments.useQuery({
        ...input,
        where: {
            ...input.where,
            reserveId,
            Compensation: {
                status: 'INITIALIZED',
            },
        },
    });

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        // A descriptive key: the claim page's own table may hold `q` underneath this overlay (CUR-1341).
        urlKey: 'payments',
        queryError: error,
        emptyState: { noRecordsTitle: 'No compensation payments linked to this reserve' },
        pagination: {
            position: 'bottom',
        },
        columns: [
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Date created',
                sortable: 'createdAt',
                cell: ({ row }) => formatDate(row.createdAt, 'dd MMM yyyy'),
            }),
            columnHelper.accessor((row) => row.CreatedBy?.firstName, {
                id: 'createdBy',
                header: 'Created by',
                sortable: 'CreatedBy.firstName',
                cell: ({ row }) => (
                    <User
                        userId={row.CreatedBy?.userId}
                        photoUrl={row.CreatedBy?.photoUrl}
                        name={row.CreatedBy?.fullName}
                    />
                ),
            }),
            columnHelper.accessor((row) => row.Compensation?.TransactionType?.name, {
                id: 'transactionType',
                header: 'Transaction type',
                sortable: 'Compensation.TransactionType.name',
                cell: ({ row }) => row.Compensation?.TransactionType?.name,
            }),
            columnHelper.accessor((row) => row.Compensation?.costCodeFullName, {
                id: 'costCode',
                header: 'Cost code',
                cell: ({ row }) => row.Compensation?.costCodeFullName,
            }),
            columnHelper.accessor('description', {
                id: 'description',
                header: 'Description',
                cell: ({ row }) => <ComponentPreview>{row.description}</ComponentPreview>,
                className: 'max-w-xs',
            }),
            columnHelper.accessor('transactionAmount', {
                id: 'transactionAmount',
                header: 'Transaction amount',
                cell: ({ row }) => formatCurrency(row.transactionAmount),
            }),
        ],
    });

    return (
        <PrismaTable
            {...tableProps}
            className="[&_.ant-table-cell]:align-top"
            onRow={(item) => ({
                className: 'cursor-pointer',
                onClick() {
                    navigate({
                        to: '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/financials/compensation-payments/details/$compensationPaymentId',
                        params: {
                            orgId,
                            id,
                            compensationId: item.Compensation.id,
                            compensationPaymentId: item.id,
                        },
                    });
                },
            })}
        />
    );
}
